import { useState } from "react";
import {
  Text,
  Pressable,
  KeyboardAvoidingView,
  Platform,
  View,
  Modal,
  TextInput,
  ActivityIndicator,
} from "react-native";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";
import { useRouter } from "expo-router";
import styled from "styled-components/native";
import { colors, radius, font } from "@/lib/theme";

const DEMO_URL =
  "https://europe-west2-crease-cricket.cloudfunctions.net/requestDemo";

const Container = styled.View`
  flex: 1;
  background-color: ${colors.white};
`;

const Content = styled.View`
  flex: 1;
  padding-horizontal: 24px;
  padding-top: 24px;
  max-width: 480px;
  width: 100%;
  align-self: center;
`;

const Title = styled.Text`
  font-size: 24px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  letter-spacing: -0.5px;
  margin-bottom: 8px;
`;

const Subtitle = styled.Text`
  font-size: 15px;
  color: ${colors.inkSecondary};
  line-height: 22px;
  margin-bottom: 28px;
`;

const Label = styled.Text`
  font-size: 11px;
  font-weight: ${font.bold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
  margin-top: 16px;
`;

const Input = styled.TextInput`
  background-color: ${colors.bg};
  border-width: 2px;
  border-color: ${colors.line};
  border-radius: ${radius.card}px;
  padding-horizontal: 16px;
  padding-vertical: 14px;
  font-size: 16px;
  color: ${colors.ink};
`;

const Hint = styled.Text`
  font-size: 13px;
  color: ${colors.inkMuted};
  margin-top: 6px;
  line-height: 18px;
`;

const ErrorText = styled.Text`
  color: ${colors.danger};
  font-size: 14px;
  font-weight: ${font.semibold};
  margin-top: 12px;
`;

const BtnText = styled.Text`
  color: #fff;
  font-size: 16px;
  font-weight: ${font.bold};
`;

const TeamChips = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const TeamChip = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 6px;
  background-color: ${colors.brand50};
  border-width: 1px;
  border-color: ${colors.brandLight};
  border-radius: ${radius.full}px;
  padding-horizontal: 14px;
  padding-vertical: 8px;
`;

const TeamChipText = styled.Text`
  font-size: 14px;
  font-weight: ${font.semibold};
  color: ${colors.brandDark};
`;

const TeamChipRemove = styled.Text`
  font-size: 16px;
  color: ${colors.inkMuted};
  font-weight: ${font.bold};
`;

const AddTeamRow = styled.View`
  flex-direction: row;
  gap: 8px;
  margin-top: 10px;
`;

const AddTeamInput = styled.TextInput`
  flex: 1;
  background-color: ${colors.bg};
  border-width: 2px;
  border-color: ${colors.line};
  border-radius: ${radius.card}px;
  padding-horizontal: 14px;
  padding-vertical: 10px;
  font-size: 15px;
  color: ${colors.ink};
`;

const AddBtnText = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.brand};
`;

export default function SignupClub() {
  const router = useRouter();

  const [name, setName] = useState("");
  const [teams, setTeams] = useState<string[]>(["1st XI", "2nd XI"]);
  const [newTeam, setNewTeam] = useState("");
  const [error, setError] = useState("");

  // Demo request state
  const [showDemo, setShowDemo] = useState(false);
  const [demoName, setDemoName] = useState("");
  const [demoEmail, setDemoEmail] = useState("");
  const [demoClub, setDemoClub] = useState("");
  const [demoSending, setDemoSending] = useState(false);
  const [demoSent, setDemoSent] = useState(false);
  const [demoError, setDemoError] = useState("");

  const addTeam = () => {
    const trimmed = newTeam.trim();
    if (!trimmed) return;
    if (teams.includes(trimmed)) {
      setNewTeam("");
      return;
    }
    setTeams([...teams, trimmed]);
    setNewTeam("");
  };

  const removeTeam = (index: number) => {
    setTeams(teams.filter((_, i) => i !== index));
  };

  const handleRequestDemo = async () => {
    if (!demoName.trim() || !demoEmail.trim() || !demoClub.trim()) return;
    setDemoSending(true);
    setDemoError("");
    try {
      const res = await fetch(DEMO_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: demoName.trim(),
          email: demoEmail.trim(),
          clubName: demoClub.trim(),
        }),
      });
      if (!res.ok) throw new Error("Failed");
      setDemoSent(true);
    } catch {
      setDemoError("Something went wrong. Please try again.");
    } finally {
      setDemoSending(false);
    }
  };

  const closeDemo = () => {
    setShowDemo(false);
    setTimeout(() => {
      setDemoName("");
      setDemoEmail("");
      setDemoClub("");
      setDemoSent(false);
      setDemoError("");
    }, 200);
  };

  const handleNext = () => {
    if (!name.trim()) {
      setError("Club name is required.");
      return;
    }

    const finalTeams = teams.length > 0 ? teams : ["Team"];

    router.push({
      pathname: "/(auth)/signup-account",
      params: {
        clubName: name.trim(),
        teams: JSON.stringify(finalTeams),
      },
    });
  };

  return (
    <Container>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === "ios" ? "padding" : "height"}
      >
        <KeyboardAwareScroll contentContainerStyle={{ flexGrow: 1 }} keyboardShouldPersistTaps="handled">
          <Content>
            <Title>Register your club</Title>
            <Subtitle>
              You're setting up a new club on inthecrease. Your players will join via invite once you're set up.
            </Subtitle>

            <Label>Club name</Label>
            <Input
              placeholder="e.g. Peterborough Town CC"
              placeholderTextColor={colors.inkMuted}
              value={name}
              onChangeText={setName}
              autoCapitalize="words"
              autoFocus
            />

            <Label>Teams</Label>
            <TeamChips>
              {teams.map((team, i) => (
                <TeamChip key={i}>
                  <TeamChipText>{team}</TeamChipText>
                  <Pressable onPress={() => removeTeam(i)} hitSlop={8}>
                    <TeamChipRemove>×</TeamChipRemove>
                  </Pressable>
                </TeamChip>
              ))}
            </TeamChips>

            <AddTeamRow>
              <AddTeamInput
                placeholder="Add a team..."
                placeholderTextColor={colors.inkMuted}
                value={newTeam}
                onChangeText={setNewTeam}
                autoCapitalize="words"
                onSubmitEditing={addTeam}
                returnKeyType="done"
              />
              <Pressable
                onPress={addTeam}
                style={({ pressed }) => [
                  {
                    backgroundColor: colors.brand50,
                    borderWidth: 2,
                    borderColor: colors.brandLight,
                    borderRadius: radius.card,
                    paddingHorizontal: 16,
                    justifyContent: "center" as const,
                  },
                  pressed && { backgroundColor: colors.brandLight },
                ]}
              >
                <AddBtnText>Add</AddBtnText>
              </Pressable>
            </AddTeamRow>
            <Hint>You can add or rename teams later in club settings.</Hint>

            {error ? <ErrorText>{error}</ErrorText> : null}

            <Pressable
              onPress={handleNext}
              style={({ pressed }) => [
                {
                  backgroundColor: colors.brand,
                  borderRadius: radius.card,
                  paddingVertical: 16,
                  alignItems: "center" as const,
                  marginTop: 28,
                  shadowColor: colors.brand,
                  shadowOffset: { width: 0, height: 4 },
                  shadowOpacity: 0.25,
                  shadowRadius: 14,
                  elevation: 4,
                },
                pressed && { opacity: 0.9 },
              ]}
            >
              <BtnText>Next →</BtnText>
            </Pressable>

            <Pressable
              onPress={() => {
                setDemoClub(name.trim());
                setShowDemo(true);
              }}
              style={({ pressed }) => [
                {
                  borderWidth: 2,
                  borderColor: colors.brand,
                  borderRadius: radius.card,
                  paddingVertical: 14,
                  alignItems: "center" as const,
                  marginTop: 12,
                },
                pressed && { opacity: 0.7 },
              ]}
            >
              <Text
                style={{
                  color: colors.brand,
                  fontSize: 15,
                  fontWeight: "700" as const,
                }}
              >
                Request a demo
              </Text>
            </Pressable>
          </Content>
        </KeyboardAwareScroll>
      </KeyboardAvoidingView>

      {/* Demo request modal */}
      <Modal visible={showDemo} animationType="slide" transparent>
        <Pressable
          style={{
            flex: 1,
            backgroundColor: "rgba(0,0,0,0.4)",
            justifyContent: "flex-end",
          }}
          onPress={closeDemo}
        >
          <Pressable
            onPress={() => {}}
            style={{
              backgroundColor: colors.white,
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              padding: 24,
              paddingBottom: 40,
            }}
          >
            {demoSent ? (
              <View style={{ alignItems: "center", paddingVertical: 20 }}>
                <Text style={{ fontSize: 48, marginBottom: 16 }}>🏏</Text>
                <Text
                  style={{
                    fontSize: 20,
                    fontWeight: "800",
                    color: colors.ink,
                    marginBottom: 8,
                  }}
                >
                  We'll be in touch!
                </Text>
                <Text
                  style={{
                    fontSize: 15,
                    color: colors.inkSecondary,
                    textAlign: "center",
                    lineHeight: 22,
                    marginBottom: 24,
                  }}
                >
                  Thanks {demoName.split(" ")[0]}, we'll reach out shortly to
                  arrange a demo for {demoClub}.
                </Text>
                <Pressable
                  onPress={closeDemo}
                  style={{
                    backgroundColor: colors.brand,
                    borderRadius: radius.card,
                    paddingVertical: 14,
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <BtnText>Done</BtnText>
                </Pressable>
              </View>
            ) : (
              <>
                <Text
                  style={{
                    fontSize: 20,
                    fontWeight: "800",
                    color: colors.ink,
                    marginBottom: 4,
                  }}
                >
                  Request a demo
                </Text>
                <Text
                  style={{
                    fontSize: 14,
                    color: colors.inkSecondary,
                    lineHeight: 20,
                    marginBottom: 20,
                  }}
                >
                  We'll walk you through how inthecrease can work for your club.
                </Text>

                <Label>Your name</Label>
                <Input
                  placeholder="e.g. John Smith"
                  placeholderTextColor={colors.inkMuted}
                  value={demoName}
                  onChangeText={setDemoName}
                  autoCapitalize="words"
                />

                <Label>Email</Label>
                <Input
                  placeholder="john@example.com"
                  placeholderTextColor={colors.inkMuted}
                  value={demoEmail}
                  onChangeText={setDemoEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                />

                <Label>Club name</Label>
                <Input
                  placeholder="e.g. Peterborough Town CC"
                  placeholderTextColor={colors.inkMuted}
                  value={demoClub}
                  onChangeText={setDemoClub}
                  autoCapitalize="words"
                />

                {demoError ? <ErrorText>{demoError}</ErrorText> : null}

                <Pressable
                  onPress={handleRequestDemo}
                  disabled={
                    demoSending ||
                    !demoName.trim() ||
                    !demoEmail.trim() ||
                    !demoClub.trim()
                  }
                  style={({ pressed }) => [
                    {
                      backgroundColor: colors.brand,
                      borderRadius: radius.card,
                      paddingVertical: 16,
                      alignItems: "center" as const,
                      marginTop: 20,
                      opacity:
                        !demoName.trim() || !demoEmail.trim() || !demoClub.trim()
                          ? 0.5
                          : 1,
                    },
                    pressed && { opacity: 0.9 },
                  ]}
                >
                  {demoSending ? (
                    <ActivityIndicator color="#fff" />
                  ) : (
                    <BtnText>Request demo</BtnText>
                  )}
                </Pressable>

                <Pressable onPress={closeDemo} style={{ marginTop: 12, alignItems: "center" }}>
                  <Text
                    style={{
                      color: colors.inkMuted,
                      fontSize: 15,
                      fontWeight: "600",
                    }}
                  >
                    Cancel
                  </Text>
                </Pressable>
              </>
            )}
          </Pressable>
        </Pressable>
      </Modal>
    </Container>
  );
}
